// util.js

// Default provider display names (fallback when metadata not loaded)
export const providers = {
    openai: 'OpenAI GPT-4o',
    claude: 'Claude 3.5 Sonnet',
    gemini: 'Gemini 1.5 Pro',
    deepseek: 'DeepSeek',
    xai: 'xAI (Grok)'
};

// Build dynamic provider list from model metadata
// Only includes chat-capable models (excludes dedicated image/video/audio/embedding models)
export const getChatProviders = (modelMetadata) => {
  if (!modelMetadata || Object.keys(modelMetadata).length === 0) {
    return providers; // fallback to static list
  }

  const chatProviders = {};
  const providerDefaults = {};

  // Find the default chat model for each provider
  // We look for models with alias "default" or common chat model names
  Object.entries(modelMetadata).forEach(([modelId, metadata]) => {
    const provider = metadata.provider;

    // Skip non-chat models (image gen, video gen, tts, embeddings)
    const modelLower = modelId.toLowerCase();
    if (modelLower.includes('dall-e') ||
        modelLower.includes('image') ||
        modelLower.includes('video') ||
        modelLower.includes('sora') ||
        modelLower.includes('veo') ||
        modelLower.includes('tts') ||
        modelLower.includes('whisper') ||
        modelLower.includes('transcribe') ||
        modelLower.includes('embedding') ||
        modelLower.includes('audio') ||
        modelLower.includes('realtime')) {
      return;
    }

    // Use only models marked as default by the backend
    if (metadata.is_default === true) {
      providerDefaults[provider] = modelId;
    }
  });

  // Build display names and pricing info from defaults
  const providerPricing = [];

  Object.entries(providerDefaults).forEach(([provider, modelId]) => {
    // Clean up model ID for display
    let displayName = modelId
      .replace(/-/g, ' ')
      .replace(/(\d{4})-(\d{2})-(\d{2})/, '') // remove dates
      .replace(/\s+/g, ' ')
      .trim();

    // Capitalize provider name
    const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);

    // Get pricing for sorting
    const modelData = modelMetadata[modelId];
    const avgCost = modelData?.pricing
      ? (modelData.pricing.input_cost_per_million + modelData.pricing.output_cost_per_million) / 2
      : 999999; // Put models without pricing at the end

    providerPricing.push({
      provider,
      displayName: `${providerName} (${displayName})`,
      avgCost
    });
  });

  // Sort by average cost (cheapest first)
  providerPricing.sort((a, b) => a.avgCost - b.avgCost);

  // Build final chatProviders object in sorted order
  providerPricing.forEach(({ provider, displayName }) => {
    chatProviders[provider] = displayName;
  });

  return Object.keys(chatProviders).length > 0 ? chatProviders : providers;
};

// Helper to convert base64 to blob (for TTS)
export const base64ToBlob = (base64, mimeType) => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }
  return new Blob([new Uint8Array(byteArrays)], { type: mimeType });
};
